"use client";

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

interface UserFormSigninPhoneType {
  phoneNumber: string;
  password: string;
}

interface UserFormSigninEmailType {
  email: string;
  password: string;
}

interface UserFormRegisterType {
  firstName: string;
  lastName: string;
  countryId: number;
  email?: string;
  phoneNumber?: string;
  password: string;
  confirmPassword?: string;
}

type AuthContextType = {
  // Sign-in related
  userFormPhone: UserFormSigninPhoneType;
  handleChange: (name: string, value: string) => void;
  userFormEmail: UserFormSigninEmailType;
  signinEmail: boolean;
  setSigninEmail: Dispatch<SetStateAction<boolean>>;
  signinPhone: boolean;
  setSigninPhone: Dispatch<SetStateAction<boolean>>;
  signinEmailOrPhone: boolean;
  setSigninEmailOrPhone: Dispatch<SetStateAction<boolean>>;

  // Register related
  registerForm: UserFormRegisterType;
  handleRegisterChange: (name: string, value: string) => void;
  registerEmail: boolean;
  setRegisterEmail: Dispatch<SetStateAction<boolean>>;
  registerPhone: boolean;
  setRegisterPhone: Dispatch<SetStateAction<boolean>>;
  registerEmailOrPhone: boolean;
  setRegisterEmailOrPhone: Dispatch<SetStateAction<boolean>>;

  // Auth state
  isAuthenticated: boolean;
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>;
  login: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof window !== "undefined") {
      const stored = sessionStorage.getItem("auth");
      setIsAuthenticated(stored === "true");
    }
  }, []);

  const login = () => {
    if (typeof navigator !== "undefined" && typeof window !== "undefined") {
      sessionStorage.setItem("auth", "true");
      setIsAuthenticated(true);
    }
  };

  const logout = () => {
    if (typeof navigator !== "undefined" && typeof window !== "undefined") {
      sessionStorage.clear();
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof window !== "undefined") {
      // Check token/session existence
      const token = sessionStorage.getItem("token");
      setIsAuthenticated(!!token);
    }
  }, []);

  // Sign-in logics ui
  const [signinEmailOrPhone, setSigninEmailOrPhone] = useState(false);
  const [signinEmail, setSigninEmail] = useState(true);
  const [signinPhone, setSigninPhone] = useState(false);

  const [userFormEmail, setUserFormEmail] = useState({
    email: "",
    password: "",
  });

  const [userFormPhone, setUserFormPhone] = useState({
    phoneNumber: "",
    password: "",
  });

  // Register logics ui
  const [registerEmailOrPhone, setRegisterEmailOrPhone] = useState(false);
  const [registerEmail, setRegisterEmail] = useState(true);
  const [registerPhone, setRegisterPhone] = useState(false);

  const [registerForm, setRegisterForm] = useState<UserFormRegisterType>({
    firstName: "",
    lastName: "",
    countryId: 0,
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (name: string, value: string) => {
    setUserFormEmail((prev) => ({
      ...prev,
      [name]: value,
    }));
    setUserFormPhone((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegisterChange = (name: string, value: string | number | unknown | undefined) => {
    setRegisterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        // Sign-in related
        login,
        logout,
        userFormPhone,
        handleChange,
        userFormEmail,
        signinEmail,
        setSigninEmail,
        signinPhone,
        setSigninPhone,
        signinEmailOrPhone,
        setSigninEmailOrPhone,

        // Register related
        registerForm,
        handleRegisterChange,
        registerEmail,
        setRegisterEmail,
        registerPhone,
        setRegisterPhone,
        registerEmailOrPhone,
        setRegisterEmailOrPhone,

        // Auth state
        isAuthenticated,
        setIsAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
};
